/* Reveal decorative content once; product forms and picker containers stay in normal flow. */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const selector = [
    '.vm-hero__desktop', '.vm-hero__mobile', '.vm-hero__content h1', '.vm-hero__content .vm-button',
    '.vm-section-heading', '.vm-product-card', '.vm-collections__grid > a', '.vm-moment',
    '.vm-story__images > div', '.vm-story__copy', '.collection-hero__text-wrapper',
    '.product__media', '.product__title > h1', '.related-products__heading', '.main-page-title',
  ].join(',');
  const seen = new WeakSet();
  const pending = new Set();
  let observer;
  let mutations;

  function finish(element) {
    pending.delete(element);
    observer?.unobserve(element);
    element.classList.remove('vm-motion-enter');
  }

  function reveal(element) {
    observer.unobserve(element);
    pending.delete(element);
    // A focused control, restored scroll position or reduced-motion preference takes precedence.
    if (reducedMotion.matches || element.contains(document.activeElement) || element.getBoundingClientRect().bottom <= 0) return;
    element.classList.add('vm-motion-enter');
    element.addEventListener('animationend', () => finish(element), { once: true });
  }

  function register(root) {
    if (!(root instanceof Element || root instanceof Document)) return;
    const elements = [...root.querySelectorAll(selector)];
    if (root instanceof Element && root.matches(selector)) elements.unshift(root);
    elements.forEach((element) => {
      if (seen.has(element)) return;
      seen.add(element);
      if (element.closest('dialog, quick-add-modal')) return;

      const grid = element.closest('.vm-product-grid, .product-grid, .vm-collections__grid, .vm-moments__rail');
      if (grid) {
        const item = Array.from(grid.children).find((child) => child === element || child.contains(element));
        const siblings = Array.from(grid.children);
        const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').filter((value) => value !== 'none').length
          || (window.matchMedia('(max-width: 749px)').matches ? 2 : 4);
        element.style.setProperty('--vm-motion-delay', `${(siblings.indexOf(item) % Math.min(columns, 4)) * 120}ms`);
      }
      element.classList.add('vm-motion-item');
      pending.add(element);
      const image = element.querySelector('img');
      if (image && !image.complete) {
        const onImageReady = () => {
          if (pending.has(element) && element.isConnected && !reducedMotion.matches) observer.observe(element);
        };
        image.addEventListener('load', onImageReady, { once: true });
        image.addEventListener('error', onImageReady, { once: true });
      } else {
        observer.observe(element);
      }
    });
  }

  function stop() {
    observer?.disconnect();
    mutations?.disconnect();
    pending.clear();
    document.querySelectorAll('.vm-motion-enter').forEach(finish);
  }

  function start() {
    // CSS never hides pending elements, so missing JS/observer support leaves every page usable.
    if (reducedMotion.matches || window.Shopify?.designMode || !('IntersectionObserver' in window)) return;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) reveal(entry.target);
      });
    }, { threshold: 0, rootMargin: '0px 0px -24px 0px' });
    register(document);

    // Collection filters and recommendations insert fresh cards without a full page load.
    mutations = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => register(node)));
      pending.forEach((element) => {
        if (!element.isConnected) finish(element);
      });
    });
    const main = document.getElementById('MainContent');
    if (main) mutations.observe(main, { childList: true, subtree: true });
    document.addEventListener('focusin', (event) => {
      const element = event.target.closest('.vm-motion-item');
      if (element) finish(element);
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) document.querySelectorAll('.vm-motion-enter').forEach(finish);
    });
    reducedMotion.addEventListener('change', (event) => {
      if (event.matches) stop();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
